import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  Play, Pause, RotateCcw, ChevronDown, ChevronUp, 
  Clock, Zap, Heart, Target, Check, Calendar,
  Droplets, Coffee, Plus, Minus, Settings, Volume2, VolumeX, Mic
} from "lucide-react";
import HydrationTracker from "@/components/HydrationTracker";
import VoiceSettings, { useVoiceCoach, VOICE_STYLES } from "@/components/VoiceSettings";
import DrillAnalyzer, { FloatingAnalyzeButton } from "@/components/DrillAnalyzer";
import CommunityMangaGen from "@/components/CommunityMangaGen";
import LineSprintLeaderboard from "@/components/LineSprintLeaderboard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EngineMonitor = () => {
  const location = useLocation();
  const [drills, setDrills] = useState([]);
  const [selectedDrill, setSelectedDrill] = useState(null);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [isActive, setIsActive] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [isResting, setIsResting] = useState(false);
  const [currentSet, setCurrentSet] = useState(1);
  const [totalSets, setTotalSets] = useState(3);
  const intervalRef = useRef(null);
  
  // Water break state
  const [isWaterBreak, setIsWaterBreak] = useState(false);
  const [waterBreakTime, setWaterBreakTime] = useState(30);
  const [showWaterReminder, setShowWaterReminder] = useState(false);
  const [timeSinceLastWater, setTimeSinceLastWater] = useState(0);
  const [totalWaterConsumed, setTotalWaterConsumed] = useState(0);
  const waterReminderRef = useRef(null);
  
  // Rest settings
  const [customRestTime, setCustomRestTime] = useState(60);
  const [showRestSettings, setShowRestSettings] = useState(false);
  
  // Voice settings
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [showVoiceSettings, setShowVoiceSettings] = useState(false);
  const [allowedVoiceStyles, setAllowedVoiceStyles] = useState(null);
  const { speak, getPhrase, settings: voiceSettings } = useVoiceCoach(allowedVoiceStyles);
  
  // AI Drill Analyzer
  const [showDrillAnalyzer, setShowDrillAnalyzer] = useState(false);
  const [lastWorkoutId, setLastWorkoutId] = useState(null);
  
  const WATER_REMINDER_INTERVAL = 600; // 10 minutes

  // Custom speak function that respects voiceEnabled
  const speakMessage = useCallback((phraseKey, customText = null) => {
    if (!voiceEnabled) return;
    speak(phraseKey, customText);
  }, [voiceEnabled, speak]);

  useEffect(() => {
    fetchDrills();
    
    // Fetch voice style access based on subscription tier
    const fetchVoiceAccess = async () => {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const response = await axios.get(`${API}/subscription/feature-access`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (response.data?.voice_styles) {
            setAllowedVoiceStyles(response.data.voice_styles);
          }
        }
      } catch (err) {
        // Silently fail — default to all styles unlocked if can't fetch
      }
    };
    fetchVoiceAccess();
    
    // Check if a drill was passed from Quick Start
    if (location.state?.selectedDrill) {
      setSelectedDrill(location.state.selectedDrill);
      setExpandedCategory(location.state.selectedDrill.category);
      toast.success(`Quick Start: ${location.state.selectedDrill.name}`);
    }
    
    // Check if a custom-built multi-drill workout was passed
    if (location.state?.customWorkout?.drills?.length > 0) {
      const first = location.state.customWorkout.drills[0];
      setSelectedDrill(first);
      setExpandedCategory(first.category);
      const total = location.state.customWorkout.drills.length;
      toast.success(
        total === 1
          ? `Starting: ${first.name}`
          : `Custom workout queued — ${total} drills. Starting with: ${first.name}`
      );
    }
    
    // Load saved hydration data
    const savedHydration = localStorage.getItem('hydrationData');
    if (savedHydration) {
      const data = JSON.parse(savedHydration);
      if (data.date === new Date().toDateString()) {
        setTotalWaterConsumed(data.intake || 0);
      }
    }
    
    // Load saved rest time preference
    const savedRestSettings = localStorage.getItem('restBreakSettings');
    if (savedRestSettings) {
      const settings = JSON.parse(savedRestSettings);
      setCustomRestTime(settings.restBetweenSets || 60);
      if (settings.voiceReminders !== undefined) {
        setVoiceEnabled(settings.voiceReminders);
      }
    }
    
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (waterReminderRef.current) clearInterval(waterReminderRef.current);
      if (window.speechSynthesis) window.speechSynthesis.cancel();
    };
  }, [location.state]);

  // Water reminder timer with voice
  useEffect(() => {
    if (isActive && !isPaused && !isWaterBreak) {
      waterReminderRef.current = setInterval(() => {
        setTimeSinceLastWater(prev => {
          const newTime = prev + 1;
          if (newTime >= WATER_REMINDER_INTERVAL && newTime % WATER_REMINDER_INTERVAL === 0) {
            setShowWaterReminder(true);
            speakMessage('waterBreak');
          }
          return newTime;
        });
      }, 1000);
    } else {
      if (waterReminderRef.current) clearInterval(waterReminderRef.current);
    }
    
    return () => {
      if (waterReminderRef.current) clearInterval(waterReminderRef.current);
    };
  }, [isActive, isPaused, isWaterBreak, speakMessage]);

  // Water break countdown
  useEffect(() => {
    if (isWaterBreak && waterBreakTime > 0) {
      const timer = setInterval(() => {
        setWaterBreakTime(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            endWaterBreak();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [isWaterBreak, waterBreakTime]);

  const startWaterBreak = () => {
    setIsWaterBreak(true);
    setWaterBreakTime(30);
    setIsPaused(true);
    if (intervalRef.current) clearInterval(intervalRef.current);
    speakMessage('waterBreak');
    toast.info('💧 Water Break! Stay hydrated.');
  };

  const endWaterBreak = () => {
    setIsWaterBreak(false);
    setShowWaterReminder(false);
    setTimeSinceLastWater(0);
    setIsPaused(false);
    if (isActive) startTimer();
    speakMessage(null, "Water break complete. Back to work!");
    toast.success('Water break over. Let\'s continue!');
  };

  const logWater = (oz) => {
    const newTotal = totalWaterConsumed + oz;
    setTotalWaterConsumed(newTotal);
    setTimeSinceLastWater(0);
    setShowWaterReminder(false);
    
    const today = new Date().toDateString();
    const data = {
      date: today,
      intake: newTotal,
      lastDrink: new Date().toISOString()
    };
    localStorage.setItem('hydrationData', JSON.stringify(data));
    
    // Voice feedback for hydration milestones
    if (newTotal >= 128 && (newTotal - oz) < 128) {
      speakMessage('milestone100');
    } else if (newTotal >= 64 && (newTotal - oz) < 64) {
      speakMessage('milestone50');
    }
    
    toast.success(`+${oz}oz water logged! Total: ${newTotal}oz`);
  };

  const fetchDrills = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/drills`, { headers });
      setDrills(response.data);
    } catch (error) {
      toast.error('Failed to load drills');
    }
  };

  const startTimer = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    
    intervalRef.current = setInterval(() => {
      setTimeRemaining(prev => {
        // Voice countdown cues
        if (prev === 31) speakMessage(isResting ? null : 'countdown30', isResting ? "30 seconds rest remaining. Get ready!" : null);
        if (prev === 11) speakMessage(isResting ? null : 'countdown10', isResting ? "10 seconds. Prepare to work!" : null);
        if (prev === 6) speakMessage(null, "5");
        
        if (prev <= 1) {
          if (isResting) {
            if (currentSet >= totalSets) {
              completeWorkout();
              return 0;
            } else {
              setIsResting(false);
              setCurrentSet(prev => prev + 1);
              speakMessage(null, `Set ${currentSet + 1}. Let's go!`);
              return selectedDrill.duration_seconds;
            }
          } else {
            setIsResting(true);
            speakMessage('setComplete');
            return customRestTime;
          }
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleStart = () => {
    if (!selectedDrill) {
      toast.error('Please select a drill first');
      return;
    }
    setIsActive(true);
    setIsPaused(false);
    setTimeRemaining(selectedDrill.duration_seconds);
    setIsResting(false);
    setCurrentSet(1);
    speakMessage('workoutStart', `Starting ${selectedDrill.name}. ${totalSets} sets. ${getPhrase('workoutStart')}`);
    startTimer();
  };

  const handlePause = () => {
    setIsPaused(true);
    if (intervalRef.current) clearInterval(intervalRef.current);
    speakMessage(null, "Workout paused.");
  };

  const handleResume = () => {
    setIsPaused(false);
    speakMessage('encouragement');
    startTimer();
  };

  const handleReset = () => {
    setIsActive(false);
    setIsPaused(false);
    setTimeRemaining(0);
    setIsResting(false);
    setCurrentSet(1);
    if (intervalRef.current) clearInterval(intervalRef.current);
    speakMessage(null, "Workout reset.");
  };

  const completeWorkout = async () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    setIsActive(false);
    speakMessage('workoutComplete');
    
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.post(`${API}/workouts`, {
        drill_ids: [selectedDrill.id],
        notes: `Completed ${totalSets} sets`
      }, { headers });
      
      setLastWorkoutId(res.data?.id);
      toast.success('Workout completed!', {
        action: {
          label: 'Undo',
          onClick: () => handleResetLastWorkout(),
        },
        duration: 8000,
      });
    } catch (error) {
      console.error('Error saving workout:', error);
    }
  };

  const handleResetLastWorkout = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/workouts/last`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLastWorkoutId(null);
      toast.success('Workout reset');
    } catch {
      toast.error('Nothing to reset');
    }
  };

  const selectDrill = async (drill) => {
    setSelectedDrill(drill);
    setExpandedCategory(null);
    toast.success(`Selected: ${drill.name}`);
    // Fetch full drill detail with step-by-step manga panels
    if (drill.has_steps) {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/drills/${drill.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data?.steps_manga) {
          setSelectedDrill(prev => ({ ...prev, steps_manga: response.data.steps_manga }));
        }
      } catch (e) {
        // Silently fail — steps are optional
      }
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const categories = [
    { 
      id: 'stamina', 
      name: 'Stamina', 
      icon: Heart, 
      color: 'text-red-500',
      bgColor: 'bg-red-500/10',
      borderColor: 'border-red-500/30',
      description: 'Build endurance and cardiovascular capacity'
    },
    { 
      id: 'explosiveness', 
      name: 'Explosiveness', 
      icon: Zap, 
      color: 'text-yellow-500',
      bgColor: 'bg-yellow-500/10',
      borderColor: 'border-yellow-500/30',
      description: 'Develop power and vertical leap'
    },
    { 
      id: 'agility', 
      name: 'Agility', 
      icon: Target, 
      color: 'text-blue-500',
      bgColor: 'bg-blue-500/10',
      borderColor: 'border-blue-500/30',
      description: 'Improve footwork and reaction time'
    }
  ];

  return (
    <div className="min-h-screen bg-[#050505] p-4 md:p-8 relative">
      <div className="noise-overlay" />
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#FF4500] mb-2">87 Conditioning Drills</p>
          <h1 className="text-5xl md:text-7xl font-heading text-white tracking-tight uppercase leading-none">
            ENGINE MONITOR
          </h1>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Left: Drill Categories */}
          <div className="space-y-4">
            <h2 className="text-sm font-heading text-white uppercase tracking-wider">Select a Drill</h2>
            
            {categories.map((category) => {
              const categoryDrills = drills.filter(d => d.category === category.id);
              const isExpanded = expandedCategory === category.id;
              const IconComponent = category.icon;
              
              return (
                <Card 
                  key={category.id}
                  className={`bg-card border-border overflow-hidden transition-all ${
                    isExpanded ? category.borderColor : ''
                  }`}
                  data-testid={`category-${category.id}`}
                >
                  {/* Category Header - Clickable */}
                  <div
                    className={`p-4 cursor-pointer hover:bg-white/5 transition-colors ${
                      isExpanded ? category.bgColor : ''
                    }`}
                    onClick={() => setExpandedCategory(isExpanded ? null : category.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-sm ${category.bgColor} flex items-center justify-center`}>
                          <IconComponent className={`w-5 h-5 ${category.color}`} />
                        </div>
                        <div>
                          <h3 className={`font-bold ${category.color}`}>{category.name}</h3>
                          <p className="text-xs text-muted-foreground">{categoryDrills.length} drills</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">
                          {categoryDrills.length}
                        </Badge>
                        {isExpanded ? (
                          <ChevronUp className="w-5 h-5 text-muted-foreground" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-muted-foreground" />
                        )}
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">{category.description}</p>
                  </div>

                  {/* Drill List - Expandable */}
                  {isExpanded && (
                    <div className="border-t border-border max-h-80 overflow-y-auto">
                      {categoryDrills.map((drill) => (
                        <div
                          key={drill.id}
                          className={`p-3 border-b border-border/50 cursor-pointer hover:bg-white/5 transition-colors ${
                            selectedDrill?.id === drill.id ? 'bg-primary/10 border-l-2 border-l-primary' : ''
                          }`}
                          onClick={() => selectDrill(drill)}
                          data-testid={`drill-${drill.id}`}
                        >
                          <div className="flex items-center gap-3">
                            {/* Drill Thumbnail */}
                            {drill.photo_url && (
                              <div className="w-12 h-12 flex-shrink-0 rounded-sm overflow-hidden border border-zinc-700">
                                <img 
                                  src={drill.photo_url} 
                                  alt={drill.name}
                                  className="w-full h-full object-cover"
                                  loading="lazy"
                                />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="font-medium text-white text-sm truncate">{drill.name}</p>
                                {selectedDrill?.id === drill.id && (
                                  <Check className="w-4 h-4 text-primary flex-shrink-0" />
                                )}
                                {drill.has_steps && (
                                  <span className="w-4 h-4 bg-orange-500/20 rounded-sm flex items-center justify-center flex-shrink-0" title="Step-by-step guide available">
                                    <Target className="w-2.5 h-2.5 text-orange-400" />
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-muted-foreground truncate mt-0.5">
                                {drill.description}
                              </p>
                            </div>
                            <div className="text-right flex-shrink-0 ml-2">
                              <p className="text-xs text-muted-foreground">
                                {formatTime(drill.duration_seconds)}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                +{formatTime(drill.rest_seconds)} rest
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              );
            })}
          </div>

          {/* Right: Timer & Controls */}
          <div className="space-y-4">
            {/* Selected Drill Card */}
            <Card className="bg-card border-border" data-testid="timer-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-heading font-bold text-white">
                  {selectedDrill ? selectedDrill.name : 'No Drill Selected'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {selectedDrill ? (
                  <>
                    {/* Drill Info */}
                    {!isActive && (
                      <div className="space-y-3">
                        {/* Drill Images - Manga Illustrations */}
                        {(selectedDrill.photo_url || selectedDrill.form_url) && (
                          <div className="grid grid-cols-2 gap-2" data-testid="drill-images">
                            {selectedDrill.photo_url && (
                              <div className="relative rounded-sm overflow-hidden border border-orange-500/30 aspect-square">
                                <img 
                                  src={selectedDrill.photo_url} 
                                  alt={`${selectedDrill.name} exercise`}
                                  className="w-full h-full object-cover"
                                  loading="lazy"
                                />
                                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                                  <span className="text-[10px] font-bold text-orange-400 uppercase tracking-wide">Exercise</span>
                                </div>
                              </div>
                            )}
                            {selectedDrill.form_url && selectedDrill.form_url !== selectedDrill.photo_url && (
                              <div className="relative rounded-sm overflow-hidden border border-orange-500/30 aspect-square">
                                <img 
                                  src={selectedDrill.form_url} 
                                  alt={`${selectedDrill.name} form guide`}
                                  className="w-full h-full object-cover"
                                  loading="lazy"
                                />
                                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                                  <span className="text-[10px] font-bold text-orange-400 uppercase tracking-wide">Form</span>
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Video demo button removed — using manga step-by-step illustrations as primary form reference */}

                        <p className="text-sm text-muted-foreground">{selectedDrill.description}</p>

                        {/* Step-by-Step Manga Panels */}
                        {selectedDrill.steps_manga && selectedDrill.steps_manga.length > 0 && (
                          <div data-testid="drill-steps-manga" className="space-y-2">
                            <div className="flex items-center gap-2">
                              <div className="w-5 h-5 bg-orange-500/20 flex items-center justify-center rounded-sm">
                                <Target className="w-3 h-3 text-orange-400" />
                              </div>
                              <span className="text-xs font-bold text-orange-400 uppercase tracking-wide">Step-by-Step Form Guide</span>
                            </div>
                            <div className={`grid ${selectedDrill.steps_manga.length <= 3 ? 'grid-cols-3' : 'grid-cols-2 lg:grid-cols-4'} gap-2`}>
                              {selectedDrill.steps_manga.map((step) => (
                                <div key={step.step} className="relative rounded-sm overflow-hidden border border-orange-500/20 group">
                                  <img
                                    src={step.image}
                                    alt={`Step ${step.step}: ${step.label}`}
                                    className="w-full aspect-square object-cover"
                                    loading="lazy"
                                  />
                                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />
                                  <div className="absolute bottom-0 left-0 right-0 p-2">
                                    <div className="flex items-center gap-1 mb-0.5">
                                      <span className="w-4 h-4 bg-orange-500 rounded-full flex items-center justify-center text-[9px] font-black text-white">
                                        {step.step}
                                      </span>
                                      <span className="text-[10px] font-black text-orange-400 uppercase">{step.label}</span>
                                    </div>
                                    <p className="text-[9px] text-zinc-300 leading-tight">{step.description}</p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Drill has steps indicator (loading) */}
                        {selectedDrill.has_steps && !selectedDrill.steps_manga && (
                          <div className="flex items-center gap-2 p-3 bg-orange-500/10 border border-orange-500/20 rounded-sm">
                            <div className="w-4 h-4 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
                            <span className="text-xs text-orange-400">Loading step-by-step form guide...</span>
                          </div>
                        )}

                        {/* Community manga CTA — when drill has step descriptions but no AI panels yet */}
                        {!selectedDrill.steps_manga && (
                          <CommunityMangaGen
                            drillName={selectedDrill.name}
                            hasStaticSteps={!!selectedDrill.has_steps}
                            onGenerated={(panels) => {
                              setSelectedDrill((prev) => ({ ...prev, steps_manga: panels }));
                            }}
                          />
                        )}

                        {/* Line Sprints leaderboard — only for the Line Sprints drill */}
                        {selectedDrill.name === "Line Sprints (8/16/32 for Time)" && (
                          <LineSprintLeaderboard />
                        )}
                        
                        <div className="grid grid-cols-2 gap-3">
                          <div className="p-3 bg-background rounded-sm text-center">
                            <Clock className="w-4 h-4 mx-auto text-primary mb-1" />
                            <p className="text-lg font-bold text-white">{formatTime(selectedDrill.duration_seconds)}</p>
                            <p className="text-xs text-muted-foreground">Work</p>
                          </div>
                          <div className="p-3 bg-background rounded-sm text-center">
                            <Pause className="w-4 h-4 mx-auto text-blue-500 mb-1" />
                            <p className="text-lg font-bold text-white">{formatTime(selectedDrill.rest_seconds)}</p>
                            <p className="text-xs text-muted-foreground">Rest</p>
                          </div>
                        </div>

                        {/* Frequency Recommendation */}
                        {selectedDrill.frequency_per_week && (
                          <div className="flex items-center gap-2 p-2 bg-accent/10 rounded-sm border border-accent/30">
                            <Calendar className="w-4 h-4 text-accent" />
                            <span className="text-sm text-accent font-medium">
                              {selectedDrill.frequency_per_week}x per week recommended
                            </span>
                          </div>
                        )}

                        {/* Sets Selector */}
                        <div className="space-y-2">
                          <p className="text-xs font-bold text-muted-foreground uppercase">Sets</p>
                          <div className="flex gap-2">
                            {[1, 2, 3, 4, 5].map((num) => (
                              <button
                                key={num}
                                onClick={() => setTotalSets(num)}
                                className={`flex-1 py-2 rounded-sm text-sm font-bold transition-colors ${
                                  totalSets === num 
                                    ? 'bg-primary text-white' 
                                    : 'bg-background text-muted-foreground hover:text-white'
                                }`}
                              >
                                {num}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Instructions */}
                        {selectedDrill.instructions && (
                          <div className="p-3 bg-background rounded-sm">
                            <p className="text-xs font-bold text-muted-foreground uppercase mb-2">Instructions</p>
                            <ul className="space-y-1">
                              {selectedDrill.instructions.slice(0, 4).map((inst, idx) => (
                                <li key={idx} className="text-xs text-muted-foreground flex items-start gap-2">
                                  <span className="text-primary">•</span>
                                  {inst}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Active Timer */}
                    {isActive && (
                      <div className="text-center space-y-4">
                        <div className={`p-8 rounded-sm ${isResting ? 'bg-blue-500/20 border-2 border-blue-500' : 'bg-primary/20 border-2 border-primary'}`}>
                          <p className={`text-sm font-bold uppercase mb-2 ${isResting ? 'text-blue-500' : 'text-primary'}`}>
                            {isResting ? 'REST' : 'WORK'}
                          </p>
                          <p className="text-6xl md:text-7xl font-heading font-black text-white font-mono" data-testid="timer-display">
                            {formatTime(timeRemaining)}
                          </p>
                          <div className="flex justify-center gap-2 mt-4">
                            {[...Array(totalSets)].map((_, i) => (
                              <div
                                key={i}
                                className={`w-3 h-3 rounded-full ${
                                  i < currentSet ? 'bg-primary' : 'bg-muted'
                                }`}
                              />
                            ))}
                          </div>
                          <p className="text-sm text-muted-foreground mt-2">
                            Set {currentSet} of {totalSets}
                          </p>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-8">
                    <Target className="w-12 h-12 mx-auto text-muted-foreground opacity-50 mb-3" />
                    <p className="text-muted-foreground">Select a drill from the categories to get started</p>
                  </div>
                )}

                {/* Controls */}
                <div className="space-y-3">
                  <div className="flex justify-center gap-3">
                    {!isActive && (
                      <Button
                        data-testid="start-button"
                        onClick={handleStart}
                        className="bg-primary hover:bg-primary/90 text-white font-bold uppercase px-8"
                        disabled={!selectedDrill}
                      >
                        <Play className="w-5 h-5 mr-2" />
                        Start
                      </Button>
                    )}
                    
                    {isActive && !isPaused && !isWaterBreak && (
                      <Button
                        data-testid="pause-button"
                        onClick={handlePause}
                        className="bg-secondary hover:bg-secondary/90 text-black font-bold uppercase px-8"
                      >
                        <Pause className="w-5 h-5 mr-2" />
                        Pause
                      </Button>
                    )}
                    
                    {isActive && isPaused && !isWaterBreak && (
                      <Button
                        data-testid="resume-button"
                        onClick={handleResume}
                        className="bg-primary hover:bg-primary/90 text-white font-bold uppercase px-8"
                      >
                        <Play className="w-5 h-5 mr-2" />
                        Resume
                      </Button>
                    )}
                    
                    {isActive && (
                      <Button
                        data-testid="reset-button"
                        onClick={handleReset}
                        variant="outline"
                        className="border-border hover:bg-white/5 font-bold uppercase px-8"
                      >
                        <RotateCcw className="w-5 h-5 mr-2" />
                        Reset
                      </Button>
                    )}

                    {/* Undo last completed workout */}
                    {!isActive && lastWorkoutId && (
                      <Button
                        data-testid="undo-last-workout-btn"
                        onClick={handleResetLastWorkout}
                        variant="outline"
                        className="border-orange-500/40 text-orange-400 hover:bg-orange-500/10 font-bold uppercase px-6"
                      >
                        <RotateCcw className="w-4 h-4 mr-2" />
                        Undo Last
                      </Button>
                    )}
                  </div>

                  {/* Water Break & Rest Settings - Show during active workout */}
                  {isActive && !isWaterBreak && (
                    <div className="flex justify-center gap-2 flex-wrap">
                      <Button
                        onClick={startWaterBreak}
                        size="sm"
                        variant="outline"
                        className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/20"
                        data-testid="water-break-btn"
                      >
                        <Droplets className="w-4 h-4 mr-1" />
                        Water Break
                      </Button>
                      <Button
                        onClick={() => setShowRestSettings(!showRestSettings)}
                        size="sm"
                        variant="outline"
                        className="border-zinc-600 text-zinc-400 hover:bg-zinc-800"
                      >
                        <Settings className="w-4 h-4 mr-1" />
                        Rest: {customRestTime}s
                      </Button>
                      <Button
                        onClick={() => setVoiceEnabled(!voiceEnabled)}
                        size="sm"
                        variant="outline"
                        className={`${voiceEnabled ? 'border-green-500/30 text-green-400' : 'border-zinc-600 text-zinc-400'}`}
                        data-testid="voice-toggle-btn"
                      >
                        {voiceEnabled ? <Volume2 className="w-4 h-4 mr-1" /> : <VolumeX className="w-4 h-4 mr-1" />}
                        Voice {voiceEnabled ? 'On' : 'Off'}
                      </Button>
                      <Button
                        onClick={() => setShowVoiceSettings(!showVoiceSettings)}
                        size="sm"
                        variant="outline"
                        className="border-purple-500/30 text-purple-400 hover:bg-purple-500/20"
                        data-testid="voice-settings-btn"
                      >
                        <Mic className="w-4 h-4 mr-1" />
                        Style
                      </Button>
                    </div>
                  )}

                  {/* Voice Settings Panel */}
                  {showVoiceSettings && (
                    <div className="mt-3">
                      <VoiceSettings 
                        onSave={() => setShowVoiceSettings(false)}
                        onClose={() => setShowVoiceSettings(false)}
                        allowedStyles={allowedVoiceStyles}
                      />
                    </div>
                  )}

                  {/* Rest Time Adjuster */}
                  {showRestSettings && (
                    <div className="p-3 bg-zinc-800/50 rounded-lg border border-zinc-700">
                      <p className="text-xs text-muted-foreground mb-2">Adjust rest time between sets:</p>
                      <div className="flex items-center gap-3 justify-center">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setCustomRestTime(Math.max(15, customRestTime - 15))}
                        >
                          <Minus className="w-4 h-4" />
                        </Button>
                        <span className="text-lg font-bold text-white w-16 text-center">{customRestTime}s</span>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setCustomRestTime(Math.min(180, customRestTime + 15))}
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="flex gap-2 justify-center mt-2">
                        {[30, 60, 90, 120].map((time) => (
                          <Badge 
                            key={time}
                            className="cursor-pointer hover:bg-blue-500/30"
                            onClick={() => setCustomRestTime(time)}
                          >
                            {time >= 60 ? `${time/60}m` : `${time}s`}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Water Break Modal */}
                  {isWaterBreak && (
                    <div className="p-4 bg-cyan-500/10 border border-cyan-500/30 rounded-lg">
                      <div className="text-center">
                        <Droplets className="w-8 h-8 mx-auto text-cyan-400 mb-2 animate-pulse" />
                        <p className="text-lg font-bold text-cyan-400 mb-1">Water Break</p>
                        <p className="text-3xl font-mono font-bold text-white mb-3">{waterBreakTime}s</p>
                        <Progress value={((30 - waterBreakTime) / 30) * 100} className="h-2 mb-3" />
                        
                        <div className="flex gap-2 justify-center mb-3">
                          <Button onClick={() => logWater(8)} size="sm" className="bg-cyan-600 hover:bg-cyan-500">
                            +8oz
                          </Button>
                          <Button onClick={() => logWater(16)} size="sm" variant="outline" className="border-cyan-500/30 text-cyan-400">
                            +16oz
                          </Button>
                          <Button onClick={() => logWater(24)} size="sm" variant="outline" className="border-cyan-500/30 text-cyan-400">
                            +24oz
                          </Button>
                        </div>
                        
                        <Button onClick={endWaterBreak} className="bg-primary hover:bg-primary/90">
                          <Play className="w-4 h-4 mr-2" />
                          Resume Workout
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Water Reminder */}
                  {showWaterReminder && !isWaterBreak && (
                    <div className="p-3 bg-cyan-500/20 border border-cyan-500/30 rounded-lg animate-pulse">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Droplets className="w-5 h-5 text-cyan-400" />
                          <span className="text-cyan-300 text-sm font-medium">Time for a water break!</span>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" onClick={startWaterBreak} className="bg-cyan-600 hover:bg-cyan-500">
                            Take Break
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setShowWaterReminder(false)}>
                            Later
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Hydration Stats */}
                  {isActive && (
                    <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Droplets className="w-3 h-3 text-cyan-400" />
                        Today: {totalWaterConsumed}oz
                      </span>
                      <span>|</span>
                      <span>
                        Last drink: {Math.floor(timeSinceLastWater / 60)}m ago
                      </span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Floating AI Analyze Button */}
      <FloatingAnalyzeButton onClick={() => setShowDrillAnalyzer(true)} />

      {/* AI Drill Analyzer Modal */}
      <DrillAnalyzer
        isOpen={showDrillAnalyzer}
        onClose={() => setShowDrillAnalyzer(false)}
        drillContext={selectedDrill?.name || ""}
      />
    </div>
  );
};

export default EngineMonitor;
